import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

// --------------------
// 🔹 Types
// --------------------
export interface Language {
  name: string;
  level: string;
}

export interface TechnicalSkillItem {
  competence: string;
  niveau: string;
}

export interface TechnicalSkillDomain {
  domaine: string;
  competences: TechnicalSkillItem[];
}

export interface Experience {
  company_name: string;
  start_date: string;
  end_date: string;
  job_title: string;
  mission: string;
  tasks_achievements: string;
  tools: string[];
  client_name: string;
  billable: boolean;
  show_in_abstract_dates?: boolean;
}

export interface FormData {
  // Personal Info
  full_name: string;
  email: string;
  phone: string;
  linkedin: string;
  photo_base64: string | null;
  job_titles: string[];
  formation_initiales: string[];
  diploma_education: string[];
  recent_education: string[];
  languages: Language[];
  localisation: string;
  a_propos: string;


  // Skills
  main_skills: string[];
  functional_skills: string[];
  technical_skills: TechnicalSkillDomain[];
  main_skill_entries: MainSkillEntry[];
  show_technical_skills: boolean;


  // Experiences
  experiences: Experience[];
}

export interface MainSkillEntry {
  name: string;
  functional_skills: string[];
}

interface User {
  id: number;
  email: string;
  name: string;
}

interface FormContextType {
  formData: FormData;
  updateFormData: (section: keyof FormData, data: any) => void;
  submitForm: () => Promise<boolean>;
  isSubmitting: boolean;
  consultantId: string | null;
  cvId: number | null;
  loadConsultantByEmail: (email: string) => Promise<boolean>;
  resetForm: () => void;
  isLoadingProfile: boolean;
  validationError: string;
  validateStep: (step: number) => boolean;
  clearValidationError: () => void;
  currentUser: User | null;
  isLoadingUser: boolean;
  loadCurrentUser: () => Promise<void>;
}

// --------------------
// 🔹 Initial Values
// --------------------
const initialFormData: FormData = {
  full_name: '',
  email: '',
  photo_base64: null,
  job_titles: [],
  formation_initiales: [],
  diploma_education: [],
  recent_education: [],
  linkedin: '',
  phone: '',
  languages: [],
  localisation: '',
  a_propos: '',
  main_skills: [],
  functional_skills: [],
  main_skill_entries: [{ name: '', functional_skills: [] }],
  technical_skills: [{ domaine: '', competences: [{ competence: '', niveau: '' }] }],
  show_technical_skills: true,
  experiences: [],
};

// --------------------
// 🔹 Context setup
// --------------------
const FormContext = createContext<FormContextType | undefined>(undefined);

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

// --------------------
// 🔹 Provider
// --------------------
interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children }) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consultantId, setConsultantId] = useState<string | null>(null);
  const [cvId, setCvId] = useState<number | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;
  const API_URL_LWT = process.env.REACT_APP_LWT_URL;

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    setIsLoadingUser(true);

    try {
      // 2️⃣ Appel authentifié
      const response = await fetch(`${API_URL_LWT}/api/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData.user);

        // Charger automatiquement le profil du consultant
        if (userData.user?.email) {
          await loadConsultantByEmail(userData.user.email);
        }
      } else {
        console.warn('Utilisateur non authentifié');
        // window.location.href = 'https://lwt.jlcconsulting.eu/auth/connexion';
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'utilisateur :", error);
      // window.location.href = 'https://lwt.jlcconsulting.eu/auth/connexion';
    } finally {
      setIsLoadingUser(false);
    }
  };

  const updateFormData = useCallback((section: keyof FormData, data: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
    if (validationError) {
      setValidationError('');
    }
  }, [validationError]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setConsultantId(null);
    setCvId(null);
    setValidationError('');
  }, []);

  const clearValidationError = useCallback(() => {
    setValidationError('');
  }, []);

  // Validation functions
  const validateStep = useCallback((step: number): boolean => {
    setValidationError('');
    
    switch (step) {
      case 0:
        if (!formData.full_name || !formData.email || !formData.localisation || !formData.a_propos || !formData.phone || !formData.linkedin) {
          setValidationError('Tous les champs obligatoires doivent être renseignés pour passer à l\'étape suivante.');
          return false;
        }
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setValidationError('Veuillez entrer une adresse email valide.');
          return false;
        }
        return true;

      case 1: // Skills
        if (formData.main_skill_entries.length === 0) {
          setValidationError('Veuillez ajouter au moins une compétence principale pour passer à l\'étape suivante.');
          return false;
        }
        return true;

      case 2: // Experiences
        if (formData.experiences.length === 0) {
          setValidationError('Veuillez ajouter au moins une expérience professionnelle pour passer à l\'étape suivante.');
          return false;
        }
        // Validate each experience has required fields
        for (const exp of formData.experiences) {
          if (!exp.company_name || !exp.job_title || !exp.start_date || !exp.mission) {
            setValidationError('Chaque expérience doit contenir : entreprise, poste, date de début et mission.');
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  }, [formData]);

  const loadConsultantByEmail = async (email: string): Promise<boolean> => {
    setIsLoadingProfile(true);
    try {
      const response = await fetch(`${API_URL}/consultant-form/search?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.consultant_id) {
          // Found existing consultant, populate form
          setConsultantId(result.consultant_id);
          setCvId(result.cv_id || null);
          setFormData({
            full_name: result.full_name || '',
            email: email,  // Use the email that was searched
            photo_base64: result.photo_base64 || null,
            job_titles: result.job_titles || [],
            diploma_education: result.diploma_education || [],
            recent_education: result.recent_education || [],
            formation_initiales: result.formation_initiales || [],
            languages: result.languages || [],
            localisation: result.localisation || '',
            phone: result.phone || '',
            linkedin: result.linkedin || '',
            a_propos: result.a_propos || '',
            main_skills: result.main_skills || [],
            main_skill_entries: result.main_skill_entries || [{ name: '', functional_skills: [] }],
            functional_skills: result.functional_skills || [],
            technical_skills: result.technical_skills || [{ domaine: '', competences: [{ competence: '', niveau: '' }] }],
            show_technical_skills: result.show_technical_skills !== undefined ? result.show_technical_skills : true,
            experiences: result.experiences || []
          });
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error loading consultant:', error);
      return false;
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const submitForm = async (): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const payload = formData

      let response;
      if (consultantId) {
        // Update existing consultant
        response = await fetch(`${API_URL}/consultant-form/update/${consultantId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new consultant
        response = await fetch(`${API_URL}/consultant-form/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        const result = await response.json();
        console.log('Form submitted successfully:', result);
        if (!consultantId && result.consultant_id) {
          setConsultantId(result.consultant_id);
        }
        if (result.cv_id) {
          setCvId(result.cv_id);
        }
        alert(`Formulaire ${consultantId ? 'mis à jour' : 'enregistré'} avec succès! ID: ${result.consultant_id || consultantId}, CV ID: ${result.cv_id || cvId}`);
        return true;
      } else {
        const error = await response.json();
        console.error('Submission error:', error);
        alert(`Erreur: ${error.detail || 'Erreur lors de l\'enregistrement'}`);
        return false;
      }

      const result = await response.json();
      console.log('✅ Form submitted successfully:', result);
      alert(`Formulaire enregistré avec succès ! ID: ${result.consultant_id}`);
      return true;
    } catch (error) {
      console.error('🌐 Network error:', error);
      alert('Erreur de connexion. Vérifiez votre connexion Internet.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContext.Provider value={{ 
      formData, 
      updateFormData, 
      submitForm, 
      isSubmitting, 
      consultantId,
      cvId,
      loadConsultantByEmail, 
      resetForm,
      isLoadingProfile,
      validationError,
      validateStep,
      clearValidationError,
      currentUser,
      isLoadingUser,
      loadCurrentUser
    }}>
      {children}
    </FormContext.Provider>
  );
};
